import React, { useState, useEffect, useCallback } from 'react';
import { getStatistics } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Statistics() {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [range, setRange] = useState(100);

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getStatistics(range);
      if (response.success) {
        setStatistics(response);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      alert('통계를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    if (showStats) {
      fetchStatistics();
    }
  }, [showStats, fetchStatistics]);

  const getFrequencyChartData = () => {
    if (!statistics || !statistics.frequency) return null;

    const numbers = Object.keys(statistics.frequency).map(Number).sort((a, b) => a - b);
    const counts = numbers.map(num => statistics.frequency[num]);

    return {
      labels: numbers,
      datasets: [
        {
          label: '출현 횟수',
          data: counts,
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  const getOddEvenChartData = () => {
    if (!statistics || !statistics.oddEvenRatio) return null;

    return {
      labels: ['홀수', '짝수'],
      datasets: [
        {
          data: [statistics.oddEvenRatio.oddCount, statistics.oddEvenRatio.evenCount],
          backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)'],
          borderColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)'],
          borderWidth: 1
        }
      ]
    };
  };

  const getHighLowChartData = () => {
    if (!statistics || !statistics.highLowRatio) return null;

    return {
      labels: ['저번호 (1-22)', '고번호 (23-45)'],
      datasets: [
        {
          data: [statistics.highLowRatio.lowCount, statistics.highLowRatio.highCount],
          backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(153, 102, 255, 0.6)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
          borderWidth: 1
        }
      ]
    };
  };

  return (
    <div className="statistics">
      <button
        className="toggle-button"
        onClick={() => setShowStats(!showStats)}
      >
        {showStats ? '통계 숨기기' : '통계 보기'}
      </button>

      {showStats && (
        <div className="stats-content">
          <div className="stats-header">
            <h2>통계 분석</h2>
            <div className="range-selector">
              <label>분석 범위:</label>
              <select value={range} onChange={(e) => setRange(Number(e.target.value))}>
                <option value={50}>최근 50회</option>
                <option value={100}>최근 100회</option>
                <option value={200}>최근 200회</option>
                <option value={500}>최근 500회</option>
              </select>
            </div>
          </div>

          {loading ? (
            <p>통계를 불러오는 중...</p>
          ) : statistics && statistics.success ? (
            <>
              <div className="stats-summary">
                <div className="stat-card">
                  <div className="stat-label">분석 회차</div>
                  <div className="stat-value">{statistics.actualCount}회</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">평균 합계</div>
                  <div className="stat-value">{statistics.averageSum}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">홀수 비율</div>
                  <div className="stat-value">{statistics.oddEvenRatio.odd}%</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">고번호 비율</div>
                  <div className="stat-value">{statistics.highLowRatio.high}%</div>
                </div>
              </div>

              <div className="top-numbers">
                <div className="top-list">
                  <h3>많이 나온 번호 (Top 10)</h3>
                  <div className="number-chips">
                    {statistics.topFrequent.map(item => (
                      <div key={item.number} className="number-chip">
                        <span className="chip-number">{item.number}</span>
                        <span className="chip-count">({item.count}회)</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="top-list">
                  <h3>적게 나온 번호 (Top 10)</h3>
                  <div className="number-chips">
                    {statistics.leastFrequent.map(item => (
                      <div key={item.number} className="number-chip overdue">
                        <span className="chip-number">{item.number}</span>
                        <span className="chip-count">({item.count}회)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="charts">
                <div className="chart-container">
                  <h3>번호별 출현 빈도</h3>
                  {getFrequencyChartData() && (
                    <Bar
                      data={getFrequencyChartData()}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false }
                        }
                      }}
                    />
                  )}
                </div>

                <div className="chart-row">
                  <div className="chart-container small">
                    <h3>홀수 vs 짝수</h3>
                    {getOddEvenChartData() && (
                      <Pie
                        data={getOddEvenChartData()}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false
                        }}
                      />
                    )}
                  </div>

                  <div className="chart-container small">
                    <h3>저번호 vs 고번호</h3>
                    {getHighLowChartData() && (
                      <Pie
                        data={getHighLowChartData()}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p>통계 데이터가 없습니다.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Statistics;
